// =============================================================================
// app/src/lib/parcelamento.ts — a conta da telinha do crédito (087).
//
// Pedido do dono (28/09/2026): escolher em quantas vezes foi a venda no
// crédito, vendo a lista com o valor de cada parcela, "para termos a
// informação da taxa de parcelamento".
//
// O QUE ESTE MÓDULO NÃO FAZ: não encarece nada. Parcelar não muda o que o
// cliente paga — a maquininha desconta da LOJA. Por isso nenhuma função aqui
// devolve um total maior que o da venda; o que elas devolvem é quanto a loja
// vai RECEBER, que é a informação que faltava.
//
// A DIVISÃO EM CENTAVOS É A PARTE TRAIÇOEIRA. R$ 100 em 3x não é 33,33 três
// vezes: é 33,34 + 33,33 + 33,33, e a maquininha cobra a diferença na
// primeira. Dividir por 3 e arredondar cada parcela dá R$ 99,99 — some um
// centavo e o dono confere o extrato contra um número que não fecha.
// =============================================================================
import type { BandeiraCartao, TaxaDeCartao } from '@core/types';

/** Como cada bandeira se chama na tela. A chave é o que vai para o banco. */
export const NOME_DA_BANDEIRA: Record<BandeiraCartao, string> = {
  visa_master: 'Visa / Master',
  elo: 'Elo',
  bari_american: 'Bari American',
  hipercard: 'Hipercard',
  diners: 'Diners',
};

export const BANDEIRAS: BandeiraCartao[] = [
  'visa_master', 'elo', 'bari_american', 'hipercard', 'diners',
];

/** Uma linha da telinha: "6x de R$ 166,67 — taxa 2,55%, a loja recebe R$ 974,50". */
export interface OpcaoDeParcela {
  parcelas: number;
  /** O valor de CADA parcela. A primeira pode ser um centavo maior. */
  valorDaParcela: number;
  /** Quanto a primeira parcela tem a mais, em centavos (0, 1 ou 2…). */
  sobraNaPrimeira: number;
  taxaPct: number;
  /** O que a maquininha desconta, em reais. */
  taxaEmReais: number;
  /** O que sobra para a loja. Derivado — não se grava (regra 4). */
  liquido: number;
}

/**
 * A taxa que vale para esta bandeira, modalidade e número de parcelas.
 *
 * Devolve `null` quando não há faixa cadastrada — e quem chama tem de tratar
 * isso como "não dá para vender assim ainda", nunca como zero: taxa zero faria
 * a venda parecer render 100%, e o erro só apareceria no extrato do mês
 * seguinte. A RPC recusa pela mesma razão.
 */
export function taxaDaFaixa(
  taxas: readonly TaxaDeCartao[],
  bandeira: BandeiraCartao,
  modalidade: 'debito' | 'credito',
  parcelas: number,
): number | null {
  const achada = taxas.find(
    (t) => t.bandeira === bandeira
      && t.modalidade === modalidade
      && parcelas >= t.parcelas_de
      && parcelas <= t.parcelas_ate,
  );
  return achada ? Number(achada.taxa_pct) : null;
}

/** Em quantas vezes esta bandeira parcela, pelo que está cadastrado. */
export function maximoDeParcelas(
  taxas: readonly TaxaDeCartao[],
  bandeira: BandeiraCartao,
): number {
  const doCredito = taxas.filter((t) => t.bandeira === bandeira && t.modalidade === 'credito');
  if (doCredito.length === 0) return 0;
  return Math.max(...doCredito.map((t) => t.parcelas_ate));
}

/** As bandeiras que têm crédito cadastrado — as outras não entram na telinha. */
export function bandeirasComCredito(taxas: readonly TaxaDeCartao[]): BandeiraCartao[] {
  return BANDEIRAS.filter((b) => maximoDeParcelas(taxas, b) > 0);
}

/**
 * A lista inteira da telinha, para uma bandeira.
 *
 * Faixa sem taxa cadastrada simplesmente NÃO VIRA LINHA: é melhor a opção não
 * existir do que existir mostrando um número inventado.
 */
export function opcoesDeParcelamento(
  taxas: readonly TaxaDeCartao[],
  bandeira: BandeiraCartao,
  total: number,
): OpcaoDeParcela[] {
  const maximo = maximoDeParcelas(taxas, bandeira);
  const opcoes: OpcaoDeParcela[] = [];
  for (let n = 1; n <= maximo; n += 1) {
    const taxaPct = taxaDaFaixa(taxas, bandeira, 'credito', n);
    if (taxaPct === null) continue;
    opcoes.push(montarOpcao(total, n, taxaPct));
  }
  return opcoes;
}

/**
 * Uma linha, com a divisão em centavos fechando no total.
 *
 * Trabalha em CENTAVOS INTEIROS de propósito: `100 / 3` em ponto flutuante já
 * chega errado, e somar três vezes o arredondado erra de novo. Aqui a sobra é
 * medida e vai para a primeira parcela, que é o que a maquininha faz.
 */
export function montarOpcao(total: number, parcelas: number, taxaPct: number): OpcaoDeParcela {
  const centavos = Math.round(total * 100);
  const porParcela = Math.floor(centavos / parcelas);
  const sobra = centavos - porParcela * parcelas;
  // A taxa também em centavos, arredondada como a adquirente arredonda.
  const taxaCentavos = Math.round((centavos * taxaPct) / 100);
  return {
    parcelas,
    valorDaParcela: porParcela / 100,
    sobraNaPrimeira: sobra,
    taxaPct,
    taxaEmReais: taxaCentavos / 100,
    liquido: (centavos - taxaCentavos) / 100,
  };
}

/**
 * "3x de R$ 33,33 (a 1ª de R$ 33,34)" — o texto que o vendedor lê para o
 * cliente. Sem a ressalva quando a divisão fecha redonda, que é o caso comum.
 */
export function textoDaParcela(o: OpcaoDeParcela, brl: (v: number) => string): string {
  const base = `${o.parcelas}x de ${brl(o.valorDaParcela)}`;
  if (o.sobraNaPrimeira === 0) return base;
  return `${base} (a 1ª de ${brl(o.valorDaParcela + o.sobraNaPrimeira / 100)})`;
}
